// La búsqueda de productos mira el nombre Y los datos del producto: SKU, análisis de IA
// (`descripcion_visual`), descripción de la tienda, características (`atributos`) y categoría
// (ADR-2026-10-21-la-busqueda-mira-los-datos-del-producto). Puro: los datos los trae
// `indiceDeBusquedaDeProductos` recién cuando alguien escribe en el buscador.
import { CATEGORIAS_DE_PRODUCTO } from './detallesDelProducto';

/** Lowercase + sin acentos: "veronica" encuentra "Verónica". */
export const normalizarParaBuscar = (s: string): string =>
  s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}/gu, '');

export interface FilaParaBuscar {
  sku?: string | null;
  descripcion?: string | null;
  descripcion_visual?: string | null;
  atributos?: unknown;
  categoria_producto?: string | null;
  /** Los caminos de las categorías de la marca que lleva ("Remeras › Oversize"). */
  categorias?: string[];
}

/** Los datos de una variante en un solo texto normalizado. */
export function textoDeBusqueda(f: FilaParaBuscar): string {
  const partes: string[] = [];
  if (f.sku) partes.push(f.sku);
  if (f.descripcion_visual) partes.push(f.descripcion_visual);
  if (f.descripcion) partes.push(f.descripcion);
  if (Array.isArray(f.atributos)) {
    for (const a of f.atributos) if (typeof a === 'string') partes.push(a);
  }
  for (const c of f.categorias ?? []) partes.push(c);
  if (f.categoria_producto) {
    // El id ("electrodomestico") y la etiqueta que ve la persona ("Pisos y revestimientos").
    partes.push(f.categoria_producto);
    const c = CATEGORIAS_DE_PRODUCTO.find((x) => x.id === f.categoria_producto);
    if (c) partes.push(c.etiqueta);
  }
  return normalizarParaBuscar(partes.join(' \n '));
}

/**
 * Filtra por la búsqueda. Cada palabra tiene que estar en el nombre o en los datos (no hace
 * falta que estén juntas: "taladro rojo" encuentra "Taladro" con "rojo" en el análisis). Los que
 * coinciden por el nombre van primero; entre ellos, y entre los que coinciden por los datos, se
 * respeta el orden de entrada (el orden elegido en la pantalla). Búsqueda vacía = lista intacta.
 */
export function buscarProductos<T>(
  items: T[],
  busqueda: string,
  nombreDe: (item: T) => string | null | undefined,
  idDe: (item: T) => string | null | undefined,
  indice?: ReadonlyMap<string, string>,
): T[] {
  const q = normalizarParaBuscar(busqueda.trim());
  if (!q) return items;
  const palabras = q.split(/\s+/).filter(Boolean);
  const porNombre: T[] = [];
  const porDatos: T[] = [];
  for (const item of items) {
    const nombre = normalizarParaBuscar(nombreDe(item) ?? '');
    if (nombre.includes(q) || palabras.every((p) => nombre.includes(p))) {
      porNombre.push(item);
      continue;
    }
    const id = idDe(item);
    const datos = id ? indice?.get(id) : undefined;
    if (!datos) continue;
    const todo = `${nombre} ${datos}`;
    if (palabras.every((p) => todo.includes(p))) porDatos.push(item);
  }
  return [...porNombre, ...porDatos];
}
